import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { LinkBreakIcon } from '@phosphor-icons/react';
import ListingView from '@plone/volto/components/theme/View/ListingView';
import {
  flattenToAppURL,
  flattenHTMLToAppURL,
} from '@plone/volto/helpers/Url/Url';
import { resetNewsPortal } from 'volto-ministerio-publico-federal/actions';
import { Attachments } from 'volto-ministerio-publico-federal/components/Attachments';
import { Images } from 'volto-ministerio-publico-federal/components/Images';
import { Links } from 'volto-ministerio-publico-federal/components/Links';
import {
  InternalLayout,
  InternalLayoutProvider,
} from 'volto-ministerio-publico-federal/components/Layouts';
import { MPFLink } from 'volto-ministerio-publico-federal/components/UI';
import { RenderBlocksWithoutTitle } from '../InternalView/RenderBlocksWithoutTitle';
import AllNews from '../AllNews';
import CollectionText from '../CollectionText';
import { LISTABLE_TYPES } from './listableTypes';

const IMAGE_TYPE = 'Image';
const FILE_TYPE = 'File';
const LINK_TYPE = 'Link';

const FolderView = ({ content }) => {
  const location = useLocation();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(resetNewsPortal());
  }, [location.pathname, dispatch]);

  if (!content) return null;

  const items = content.items || [];

  const hasCollectionChild = items.some(
    (item) => item['@type'] === 'Collection',
  );

  if (hasCollectionChild) {
    return <AllNews content={content} hideForm />;
  }

  const defaultPageItem = items.find((item) => {
    const itemId = item['@id'].split('/').pop();
    return itemId === content.defaultPage;
  });

  const blocks = content.blocks || {};
  const blocksLayout = content.blocks_layout?.items || [];

  const htmlBlocks = blocksLayout
    .map((id) => blocks[id])
    .filter((b) => b?.['@type'] === 'html');

  // `blocks`/`blocks_layout` ficam de fora de propósito: o ListingView recalcula
  // os blocos de html a partir deles e, se achá-los, retorna só o html e
  // nunca chega a renderizar a listagem.
  const {
    blocks: _blocks,
    blocks_layout: _blocksLayout,
    ...contentNoBlocks
  } = content;

  // Cada tipo com visualização própria: imagens em grade, arquivos no
  // componente de anexos e links na lista dedicada; o restante segue para o
  // ListingView.
  const imageItems = items.filter((item) => item['@type'] === IMAGE_TYPE);
  const fileItems = items.filter((item) => item['@type'] === FILE_TYPE);
  const linkItems = items.filter((item) => item['@type'] === LINK_TYPE);
  const listableItems = items.filter((item) =>
    LISTABLE_TYPES.includes(item['@type']),
  );

  const renderItemSections = () => (
    <>
      <Images items={imageItems} />
      <Attachments items={fileItems} />
      <Links items={linkItems} />
      {listableItems.length > 0 && (
        <ListingView content={{ ...contentNoBlocks, items: listableItems }} />
      )}
    </>
  );

  let mainContent;

  if (
    listableItems.length === 0 &&
    htmlBlocks.length === 0 &&
    !defaultPageItem
  ) {
    mainContent = (
      <div id="page-document" className="mpf-content-internal__editor-content">
        <RenderBlocksWithoutTitle content={content} />
        {renderItemSections()}
      </div>
    );
  } else {
    mainContent = (
      <div id="page-document" className="mpf-content-internal__editor-content">
        {content.defaultPage && defaultPageItem && (
          <div className="mpf-default-page-banner">
            <LinkBreakIcon size={32} />
            {' A visualização padrão desta pasta é: '}
            <MPFLink href={flattenToAppURL(defaultPageItem['@id'])}>
              {defaultPageItem.title}
            </MPFLink>
          </div>
        )}
        {htmlBlocks &&
          htmlBlocks.map((block, i) => (
            <div
              key={i}
              dangerouslySetInnerHTML={{
                __html: flattenHTMLToAppURL(block.html),
              }}
            />
          ))}
        <CollectionText content={content} />
        {renderItemSections()}
      </div>
    );
  }

  return (
    <InternalLayoutProvider
      title={content.title}
      description={content.description}
      relatedItems={content.relatedItems}
      ocultarTitulo={content.ocultar_titulo}
      reviewState={content.review_state}
    >
      <InternalLayout>{mainContent}</InternalLayout>
    </InternalLayoutProvider>
  );
};

export default FolderView;
